/**
 * Language Mix Card (Iter319) — Agency Dashboard.
 *
 * Renders the distribution of `preferred_lang` across the tenant's Smith
 * prospects. Data source: `GET /api/smith/outreach/language-analytics`.
 *
 * Visual: 3 horizontal bars (PT / EN / ES) each showing count + %. Below,
 * a "with-preference vs auto-detected" split so the ops team knows how
 * much of the base is being decided by heuristic vs a committed manual
 * override.
 *
 * Super-admin extra: a Bulk Reset button opens a lightweight modal that
 * calls POST `/lead-preferences/bulk-reset` — used when the agency pivots
 * strategy (e.g. force EN across every ES-pinned lead).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Languages, Loader2, RotateCcw, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG = { pt: "\u{1F1E7}\u{1F1F7}", en: "\u{1F1FA}\u{1F1F8}", es: "\u{1F1EA}\u{1F1F8}" };
const BAR_COLOR = { pt: "#22C55E", en: "#3B82F6", es: "#EAB308" };
const LABEL_LANG = { pt: "Português", en: "English", es: "Español" };

export function LanguageMixCard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "master";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/language-analytics`);
      setData(data);
    } catch (_e) { /* silent — dashboard tolerates missing tile */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="rounded-2xl border overflow-hidden"
             data-testid="agency-language-mix-card"
             style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
      <div className="px-5 py-3 border-b flex items-center justify-between"
           style={{ borderColor: "rgba(220,38,38,0.20)" }}>
        <div className="flex items-center gap-2">
          <Languages className="w-4 h-4 text-[#8B5CF6]" />
          <h3 className="font-extrabold text-white text-[14px]">Language Mix</h3>
          {data && (
            <span className="text-[10.5px] text-slate-500 font-bold">
              · {data.total.toLocaleString()} prospects
            </span>
          )}
        </div>
        {isSuperAdmin && (
          <button type="button" onClick={() => setResetOpen(true)}
            data-testid="agency-language-reset-btn"
            className="h-7 px-2.5 rounded-md text-[10.5px] font-bold text-slate-300 hover:text-white border border-white/15 hover:border-[#8B5CF6] inline-flex items-center gap-1.5">
            <RotateCcw className="w-3 h-3" /> Bulk Reset
          </button>
        )}
      </div>
      <div className="px-5 py-4 space-y-3">
        {loading && (
          <div className="py-6 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#8B5CF6]" />
          </div>
        )}
        {!loading && data && (
          <>
            <div className="space-y-2" data-testid="agency-language-mix-bars">
              {data.buckets.map((b) => (
                <div key={b.lang} data-testid={`agency-language-bar-${b.lang}`}>
                  <div className="flex items-center gap-2 text-[11.5px] mb-0.5">
                    <span className="text-[14px] leading-none">{FLAG[b.lang]}</span>
                    <span className="font-extrabold text-white uppercase">{b.lang}</span>
                    <span className="text-slate-400">· {LABEL_LANG[b.lang]}</span>
                    <span className="ml-auto tabular-nums font-bold text-white">
                      {b.count.toLocaleString()}
                    </span>
                    <span className="text-slate-500 tabular-nums w-12 text-right">
                      {b.pct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded bg-white/5 overflow-hidden">
                    <div className="h-full transition-all"
                         style={{ width: `${Math.max(b.pct, 0.5)}%`,
                                  background: BAR_COLOR[b.lang] }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 text-[10.5px] pt-2 border-t border-white/5">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-slate-400">With preference:</span>
                <b className="text-white tabular-nums">{data.with_preference.toLocaleString()}</b>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-slate-500" />
                <span className="text-slate-400">Auto-detected:</span>
                <b className="text-white tabular-nums">{data.auto_detected.toLocaleString()}</b>
              </span>
            </div>
          </>
        )}
      </div>
      {resetOpen && (
        <BulkResetModal onClose={() => setResetOpen(false)}
                        onDone={() => { setResetOpen(false); load(); }} />
      )}
    </section>
  );
}

function BulkResetModal({ onClose, onDone }) {
  const [onlyCurrent, setOnlyCurrent] = useState("");
  const [setTo, setSetTo] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const body = {};
      if (onlyCurrent) body.only_current_lang = onlyCurrent;
      if (setTo) body.set_to = setTo;
      const { data } = await axios.post(`${API}/smith/outreach/lead-preferences/bulk-reset`, body);
      toast.success(`${data.mode === "clear" ? "Cleared" : "Reassigned"} preference on ${data.modified} prospect(s)`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Bulk reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         onClick={onClose}
         data-testid="agency-language-reset-modal">
      <div className="w-full max-w-md rounded-2xl border p-5 space-y-4"
           style={{ background: "#0B1120", borderColor: "rgba(139,92,246,0.35)" }}
           onClick={(ev) => ev.stopPropagation()}>
        <header className="flex items-center gap-3">
          <Languages className="w-5 h-5 text-[#8B5CF6]" />
          <h4 className="text-[14px] font-extrabold text-white flex-1">Bulk Reset Language</h4>
          <button onClick={onClose}
                  data-testid="agency-language-reset-close"
                  className="w-7 h-7 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </header>
        <p className="text-[11.5px] text-slate-400 leading-relaxed">
          Match prospects by their current preference, then either <b>clear</b> it (auto-detect will run again) or <b>reassign</b> them to a fixed language. Prospects with no preference set are never touched.
        </p>
        <div>
          <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 block mb-1">
            Match prospects currently pinned to
          </label>
          <select value={onlyCurrent} onChange={(e) => setOnlyCurrent(e.target.value)}
                  data-testid="agency-language-reset-only-current"
                  className="w-full h-9 px-3 rounded-lg bg-black/40 border border-white/15 text-[12.5px] text-white">
            <option value="">Any language (all pinned)</option>
            <option value="pt">🇧🇷 Portuguese</option>
            <option value="en">🇺🇸 English</option>
            <option value="es">🇪🇸 Spanish</option>
          </select>
        </div>
        <div>
          <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 block mb-1">
            Action
          </label>
          <select value={setTo} onChange={(e) => setSetTo(e.target.value)}
                  data-testid="agency-language-reset-set-to"
                  className="w-full h-9 px-3 rounded-lg bg-black/40 border border-white/15 text-[12.5px] text-white">
            <option value="">Clear preference (heuristic will decide)</option>
            <option value="pt">Reassign to 🇧🇷 Portuguese</option>
            <option value="en">Reassign to 🇺🇸 English</option>
            <option value="es">Reassign to 🇪🇸 Spanish</option>
          </select>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button type="button" onClick={onClose}
                  data-testid="agency-language-reset-cancel"
                  className="h-9 px-4 rounded-lg text-[12px] font-bold text-slate-300 hover:text-white">
            Cancel
          </button>
          <button type="button" onClick={run} disabled={busy}
                  data-testid="agency-language-reset-run"
                  className="h-9 px-4 rounded-lg text-[12px] font-extrabold text-white inline-flex items-center gap-2 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Run
          </button>
        </div>
      </div>
    </div>
  );
}
